import { useEffect, useState, useSyncExternalStore } from 'react';
import { alphaBlend, parseColor, toHex, withAlpha, type Rgba } from './color';

// Applying the theme to <html> (spec 01 §2): `data-theme="light|dark"` for an
// explicit choice, no attribute for "system" (tokens.css follows
// prefers-color-scheme). Persisting the preference belongs to `settings.ts`.

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const THEME_COLORS: Record<ResolvedTheme, string> = { light: '#FAFAFA', dark: '#181816' };
const DARK_QUERY = '(prefers-color-scheme: dark)';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Theme a pending cross-fade will commit once the old frame is captured (undefined: none pending). */
let pendingTheme: ResolvedTheme | null | undefined;
let transitionSequence = 0;

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/** The theme currently in effect on <html>. */
export function resolvedTheme(): ResolvedTheme {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr;
  return systemPrefersDark() ? 'dark' : 'light';
}

/** What a frosted header is composited over (FrostedSurface `base`). */
export type StatusBarBase = 'canvas' | 'white' | 'none';

export interface StatusBarTint {
  base: StatusBarBase;
  /** 0..1 frosted scroll-under progress. */
  progress: number;
}

const statusBarTints = new Map<object, StatusBarTint>();

/**
 * In the installed app the status bar (iOS `default` style, Android) is a strip
 * outside the page painted in theme-color. A frosted header pinned under it
 * registers its look here (null to withdraw) so the strip takes the header's
 * live colour as it frosts on scroll instead of staying the canvas. Only the
 * visible screen's header registers; the latest registration wins.
 */
export function setStatusBarTint(owner: object, tint: StatusBarTint | null): void {
  if (tint) {
    const previous = statusBarTints.get(owner);
    if (previous && previous.base === tint.base && previous.progress === tint.progress) return;
    statusBarTints.set(owner, tint);
  } else if (!statusBarTints.delete(owner)) {
    return;
  }
  if (typeof document !== 'undefined') syncThemeColorMeta(resolvedTheme());
}

/** The registered header's colour at the status bar (FrostedSurface.module.css fill over its base), or null. */
function statusBarColor(): string | null {
  let tint: StatusBarTint | undefined;
  for (const latest of statusBarTints.values()) tint = latest;
  if (!tint || typeof getComputedStyle !== 'function') return null;
  const tokens = getComputedStyle(document.documentElement);
  const token = (name: string) => tokens.getPropertyValue(name).trim();
  const canvas = token('--color-canvas-rgb');
  if (!canvas) return null;
  const p = Math.min(1, Math.max(0, tint.progress));
  const under = tint.base === 'white' ? token('--color-browse-header-base') || `rgb(${canvas})` : `rgb(${canvas})`;
  const fill =
    tint.base === 'none'
      ? withAlpha(`rgb(${token('--color-surface-overlay-rgb')})`, (Number(token('--color-surface-overlay-alpha')) || 1) * p)
      : withAlpha(`rgb(${token(tint.base === 'white' ? '--color-frost-white-rgb' : '--color-frost-canvas-rgb')})`, p);
  return toHex(alphaBlend(fill, under));
}

const statusBarBarriers = new Map<object, number>();

/**
 * A modal barrier (Dialog, BottomSheet, DraggableSheet, or a feature overlay)
 * registers its current opacity here (0..1 of `--color-barrier`, null to
 * withdraw) so the installed app's status bar dims with the page instead of
 * staying bright above the scrim. Call it from the barrier's animation frames
 * (`onUpdate`) so the strip fades in and out with it; stacked barriers
 * compound like the stacked scrims do. See `useStatusBarBarrier`.
 */
export function setStatusBarBarrier(owner: object, opacity: number | null): void {
  if (opacity != null && opacity > 0) {
    const next = Math.min(1, opacity);
    if (statusBarBarriers.get(owner) === next) return;
    statusBarBarriers.set(owner, next);
  } else if (!statusBarBarriers.delete(owner)) {
    return;
  }
  if (typeof document !== 'undefined') syncThemeColorMeta(resolvedTheme());
}

/** The combined scrim over the status bar (`--color-barrier` per registered opacity), or null. */
function barrierColor(): Rgba | null {
  if (statusBarBarriers.size === 0) return null;
  let barrier: Rgba = { r: 0, g: 0, b: 0, a: 0.54 };
  if (typeof getComputedStyle === 'function') {
    const token = getComputedStyle(document.documentElement).getPropertyValue('--color-barrier').trim();
    if (token) {
      try {
        barrier = parseColor(token);
      } catch {
        // keep the spec default
      }
    }
  }
  // Each layer lets (1 - alpha) of what is under it through.
  let clear = 1;
  for (const opacity of statusBarBarriers.values()) clear *= 1 - barrier.a * opacity;
  return clear >= 1 ? null : withAlpha(barrier, 1 - clear);
}

function syncThemeColorMeta(theme: ResolvedTheme): void {
  const tint = statusBarColor();
  const scrim = barrierColor();
  const dim = (color: string) => (scrim ? toHex(alphaBlend(scrim, color)) : color);
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  metas.forEach((meta) => {
    const media = meta.getAttribute('media');
    // A header tint or an explicit choice: every meta shows it. System: restore per-media values.
    const base =
      tint ??
      (!media || document.documentElement.hasAttribute('data-theme')
        ? THEME_COLORS[theme]
        : media.includes('dark')
          ? THEME_COLORS.dark
          : THEME_COLORS.light);
    const next = dim(base);
    if (meta.content !== next) meta.content = next;
  });
  // iOS 26 may take the strip from the root background rather than theme-color;
  // <html> is covered by <body>, so this only shows there.
  document.documentElement.style.backgroundColor = tint || scrim ? dim(tint ?? THEME_COLORS[theme]) : '';
}

function commitTheme(next: ResolvedTheme | null): void {
  const root = document.documentElement;
  if (next) root.setAttribute('data-theme', next);
  else root.removeAttribute('data-theme');
  syncThemeColorMeta(resolvedTheme());
  notify();
}

function canCrossFade(): boolean {
  if (typeof document.startViewTransition !== 'function') return false;
  return !(typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches);
}

/**
 * Applies a theme mode. With `animate`, the page cross-fades for 200 ms linear
 * (a View Transition, see global.css), which matches the colour lerp of
 * Flutter's AnimatedTheme without overriding any element's own transitions.
 * Without View Transitions support or with reduced motion it switches at once.
 * The animated switch commits on the next frame (after the old frame is captured).
 */
export function applyTheme(mode: ThemeMode, options: { animate?: boolean } = {}): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const next = mode === 'system' ? null : mode;
  if (pendingTheme !== undefined) {
    // A cross-fade is about to commit; let it commit the newest choice.
    pendingTheme = next;
    return;
  }
  if (root.getAttribute('data-theme') === next) return;
  if (!options.animate || !canCrossFade()) {
    commitTheme(next);
    return;
  }
  pendingTheme = next;
  const sequence = ++transitionSequence;
  root.setAttribute('data-theme-transition', '');
  const transition = document.startViewTransition(() => {
    const target = pendingTheme;
    pendingTheme = undefined;
    if (target !== undefined && root.getAttribute('data-theme') !== target) commitTheme(target);
  });
  transition.ready.catch(() => undefined);
  transition.finished
    .catch(() => undefined)
    .finally(() => {
      if (sequence === transitionSequence) root.removeAttribute('data-theme-transition');
    });
}

const listeners = new Set<() => void>();
let observing = false;

function notify(): void {
  listeners.forEach((listener) => listener());
}

function ensureObservers(): void {
  if (observing || typeof window === 'undefined') return;
  observing = true;
  if (typeof window.matchMedia === 'function') {
    window.matchMedia(DARK_QUERY).addEventListener?.('change', () => {
      if (!document.documentElement.hasAttribute('data-theme')) syncThemeColorMeta(resolvedTheme());
      notify();
    });
  }
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(notify).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  }
}

function subscribe(listener: () => void): () => void {
  ensureObservers();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders when the effective theme changes. */
export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, resolvedTheme, () => 'light');
}

export function useIsDark(): boolean {
  return useResolvedTheme() === 'dark';
}

/**
 * Registers a modal barrier with the status bar for as long as the caller is
 * mounted: returns an `onUpdate`-style callback to feed the barrier's live
 * opacity (e.g. motion's `onUpdate={(v) => report(Number(v.opacity))}`), and
 * withdraws on unmount.
 */
export function useStatusBarBarrier(): (opacity: number) => void {
  const [owner] = useState(() => ({}));
  useEffect(() => () => setStatusBarBarrier(owner, null), [owner]);
  return reportFor(owner);
}

const reporters = new WeakMap<object, (opacity: number) => void>();
function reportFor(owner: object): (opacity: number) => void {
  let report = reporters.get(owner);
  if (!report) {
    report = (opacity: number) => setStatusBarBarrier(owner, Number.isFinite(opacity) ? opacity : null);
    reporters.set(owner, report);
  }
  return report;
}
